import type { Components } from '@/api/generated/api';
import {
  TIMELINE_ACTIVITY_TYPES,
  activityDirection,
  activityTargetKind,
  type SupportMeasureActivity,
} from '@/composables/supportMeasureEntry/supportMeasureActivity.utils';
import { groupOrderedTimelineItemsByWeek } from '@/core/timeline/timeline.utils';
import { dateToTimelineDayKey } from '@/utils/date.timeline.utils';
import { immutableSort } from 'common/utils/array.utils';

export type TSupportMeasureProtocolEntry = Components.Schemas.SupportMeasureProtocolEntryDto;

export type TSupportMeasureTimelineItem =
  | {
      kind: 'protocol';
      key: string;
      date: string;
      createdAt: string;
      protocol: TSupportMeasureProtocolEntry;
    }
  | {
      kind: 'activity';
      key: string;
      date: string;
      createdAt: string;
      activities: SupportMeasureActivity[];
    }
  | { kind: 'completion'; key: string; date: string; createdAt: string };

interface IBuildSupportMeasureTimelineItemsParams {
  activities: readonly SupportMeasureActivity[];
  completedAt: string | null;
  protocol: readonly TSupportMeasureProtocolEntry[];
  selectedStudentId: number | null;
}

const getVisibleProtocolEntries = (
  protocol: readonly TSupportMeasureProtocolEntry[],
  selectedStudentId: number | null,
): TSupportMeasureProtocolEntry[] =>
  selectedStudentId == null
    ? [...protocol]
    : protocol.filter((entry) => entry.students.some((student) => student.id === selectedStudentId));

const groupActivities = (activities: readonly SupportMeasureActivity[]): SupportMeasureActivity[][] => {
  const activitiesByKey = new Map<string, SupportMeasureActivity[]>();
  for (const activity of activities) {
    // The target kind belongs in the key: studentRemoved and competenceRemoved share a direction, and a
    // mixed group would be labelled and counted as whichever kind happened to come first.
    const key =
      activity.groupId != null
        ? `${activity.groupId}:${activityDirection(activity)}:${activityTargetKind(activity)}`
        : `s${activity.id}`;
    const group = activitiesByKey.get(key);
    if (group) group.push(activity);
    else activitiesByKey.set(key, [activity]);
  }
  return [...activitiesByKey.values()];
};

/**
 * Groups first, then filters: a group stays visible when it involves the selected student, but keeps
 * its other members so the label can read "Bea +2 weitere" instead of naming Bea alone.
 */
const getVisibleActivityGroups = (
  activities: readonly SupportMeasureActivity[],
  selectedStudentId: number | null,
): SupportMeasureActivity[][] =>
  groupActivities(activities.filter((activity) => TIMELINE_ACTIVITY_TYPES.includes(activity.type))).filter(
    (group) => selectedStudentId == null || group.some((activity) => activity.studentId === selectedStudentId),
  );

const toActivityTimelineItem = (activities: SupportMeasureActivity[]): TSupportMeasureTimelineItem => {
  const latestActivity = activities.reduce((latest, activity) =>
    Date.parse(activity.createdAt) > Date.parse(latest.createdAt) ? activity : latest,
  );
  return {
    kind: 'activity',
    key: `a${activities.map((activity) => activity.id).join('-')}`,
    date: latestActivity.date,
    createdAt: latestActivity.createdAt,
    activities,
  };
};

const compareTimelineItemsDescending = (
  left: TSupportMeasureTimelineItem,
  right: TSupportMeasureTimelineItem,
): number => {
  const leftDay = dateToTimelineDayKey(left.date);
  const rightDay = dateToTimelineDayKey(right.date);
  return rightDay.localeCompare(leftDay) || Date.parse(right.createdAt) - Date.parse(left.createdAt);
};

export const buildSupportMeasureTimelineItems = ({
  activities,
  completedAt,
  protocol,
  selectedStudentId,
}: IBuildSupportMeasureTimelineItemsParams): TSupportMeasureTimelineItem[] => {
  const completionItems: TSupportMeasureTimelineItem[] = completedAt
    ? [{ kind: 'completion', key: 'completion', date: completedAt, createdAt: completedAt }]
    : [];
  const protocolItems: TSupportMeasureTimelineItem[] = getVisibleProtocolEntries(protocol, selectedStudentId).map(
    (entry) => ({
      kind: 'protocol',
      key: `p${entry.id}`,
      date: entry.date,
      createdAt: entry.createdAt,
      protocol: entry,
    }),
  );
  const activityItems = getVisibleActivityGroups(activities, selectedStudentId).map(toActivityTimelineItem);
  return immutableSort([...completionItems, ...protocolItems, ...activityItems], compareTimelineItemsDescending);
};

export const groupSupportMeasureTimelineItems = (
  items: readonly TSupportMeasureTimelineItem[],
): Array<[string, TSupportMeasureTimelineItem[]]> => groupOrderedTimelineItemsByWeek(items, (item) => item.date);
